import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { TV_CHANNEL_LANGUAGES } from "../../constants/tvChannelLanguages";

const normalizeUrlForParse = (value) => {
  const v = value.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const isSafeHttpUrl = (value) => {
  const normalized = normalizeUrlForParse(value);
  if (!normalized) return false;
  try {
    const u = new URL(normalized);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const validationSchema = Yup.object().shape({
  tvChannelName: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .required("Channel name is required")
    .min(2, "Channel name must be at least 2 characters")
    .max(120, "Channel name must be at most 120 characters"),
  tvChannelUrl: Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .required("URL is required")
    .min(4, "URL is too short")
    .max(2048, "URL must be at most 2048 characters")
    .test("valid-url", "Enter a valid URL (e.g. https://youtube.com/watch?v=…)", (v) =>
      isSafeHttpUrl(v || ""),
    )
    .test(
      "has-host",
      "URL must include a domain (e.g. youtube.com)",
      (v) => {
        if (!v || !isSafeHttpUrl(v)) return true;
        try {
          const host = new URL(normalizeUrlForParse(v)).hostname;
          return host.includes(".") || host === "localhost";
        } catch {
          return false;
        }
      },
    ),
  language: Yup.string()
    .required("Language is required")
    .oneOf(TV_CHANNEL_LANGUAGES, "Select a valid language"),
});

const emptyValues = {
  tvChannelName: "",
  tvChannelUrl: "",
  language: "ENGLISH",
  isEdit: false,
};

const SaveTvChannelModal = ({ show, handleClose, editData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { AsyncPostApiCall, AsyncPatchApiCall } = useApi();

  const formik = useFormik({
    initialValues: emptyValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        const { isEdit, ...rest } = values;
        const payload = {
          tvChannelName: String(rest.tvChannelName || "").trim(),
          tvChannelUrl: String(rest.tvChannelUrl || "").trim(),
          language: rest.language,
        };
        setLoading(true);
        let result;
        if (isEdit && editData?._id) {
          result = await AsyncPatchApiCall(
            `/tvchannel/update/${editData._id}`,
            payload,
          );
        } else {
          result = await AsyncPostApiCall("/tvchannel/create", payload);
        }

        if (result?.success) {
          toast.success(
            isEdit ? "TV channel updated successfully" : "TV channel created successfully",
          );
          onSuccess?.();
          handleClose();
        }
        else {
          toast.error(result?.message || "Something went wrong");
        }
        setLoading(false);
      } catch (error) {
        console.error("Something went wrong", error)
      }
    },
  });

  useEffect(() => {
    if (!show) return;
    if (editData) {
      formik.resetForm({
        values: {
          tvChannelName: editData.tvChannelName ?? "",
          tvChannelUrl: editData.tvChannelUrl ?? "",
          language: TV_CHANNEL_LANGUAGES.includes(editData.language)
            ? editData.language
            : "ENGLISH",
          isEdit: true,
        },
      });
    } else {
      formik.resetForm({ values: { ...emptyValues, isEdit: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync modal open + editData only
  }, [show, editData]);

  return (
    <>
      {loading && <Loader />}
      <Modal show={show} onHide={handleClose} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editData ? "Edit TV channel" : "Add TV channel"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Form.Label>
                Channel name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="tvChannelName"
                placeholder="Channel name"
                autoComplete="off"
                maxLength={120}
                {...formik.getFieldProps("tvChannelName")}
                isInvalid={
                  (formik.touched.tvChannelName || formik.submitCount > 0) &&
                  !!formik.errors.tvChannelName
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.tvChannelName}
              </Form.Control.Feedback>
            </div>
            <div className="mb-3">
              <Form.Label>
                URL <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="tvChannelUrl"
                placeholder="https://www.youtube.com/watch?v=…"
                autoComplete="off"
                maxLength={2048}
                {...formik.getFieldProps("tvChannelUrl")}
                isInvalid={
                  (formik.touched.tvChannelUrl || formik.submitCount > 0) &&
                  !!formik.errors.tvChannelUrl
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.tvChannelUrl}
              </Form.Control.Feedback>
            </div>
            <div className="mb-3">
              <Form.Label>
                Language <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="language"
                {...formik.getFieldProps("language")}
                isInvalid={
                  (formik.touched.language || formik.submitCount > 0) &&
                  !!formik.errors.language
                }
              >
                {TV_CHANNEL_LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formik.errors.language}
              </Form.Control.Feedback>
            </div>
            <div className="d-flex justify-content-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {editData ? "Update" : "Create"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SaveTvChannelModal;
